// /onboarding/phone — the number of the NEW account (board 25 v2
// erp/onboarding/phone--default, --invalid, --exists, --sending; copy
// verbatim). The format is checked locally (no request); the server decides
// the rest: an unknown number gets a code over WhatsApp (business-enrollment),
// a number that already has an account is told to sign in instead.
// States: default · invalid · sending · exists · locked · error · closed (the
// platform's allow_signups switch is off) — plus the shell's error-network /
// error-server card.

import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, LogIn } from 'lucide-react';
import { Button, PrimaryAction } from '@/components/Button';
import { isApiError } from '@/lib/api';
import { AuthError, sessionStore } from '@/lib/auth';
import { useConfigQuery } from '@/lib/config';
import { normalizeIraqiPhone } from '@/lib/digits';
import { useLayout } from '@/lib/layout';
import { PHONE, SHELL } from '../copy';
import { failureOf, footerLinksFromConfig, signupsAllowed } from '../data/api';
import { ONB, onboardingAvailable } from '../data/flow';
import { Msg, PhoneBig, phoneDigits } from '../components/widgets';
import { Wizard, type WizardError } from '../components/Wizard';
import { onboardingStore, useOnboarding } from '../state';

type Status = 'default' | 'invalid' | 'exists' | 'sending' | 'locked' | 'error';
type SysError = { kind: 'network' | 'server'; ref: string | null; detail?: string | null };

function ConsentLine({ terms, privacy }: { terms?: string; privacy?: string }) {
  const word = (label: string, href?: string) =>
    href ? (
      <a href={href} target="_blank" rel="noreferrer">
        {label}
      </a>
    ) : (
      label
    );
  return (
    <span className="onb-consent">
      {SHELL.consentBefore}
      {word(SHELL.terms, terms)}
      {SHELL.consentAnd}
      {word(SHELL.privacy, privacy)}
      {SHELL.consentAfter}
    </span>
  );
}

export function PhoneScreen() {
  const navigate = useNavigate();
  const layout = useLayout();
  const mobile = layout === 'phone';
  const notice = (useLocation().state as { notice?: string } | null)?.notice;
  const { intent, createdId } = useOnboarding();
  const config = useConfigQuery();
  const [digits, setDigits] = useState(() => phoneDigits(onboardingStore.enrollment().phone));
  const [status, setStatus] = useState<Status>('default');
  const [message, setMessage] = useState<string | null>(null);
  const [sysError, setSysError] = useState<SysError | null>(null);

  if (!onboardingAvailable()) return <Navigate to="/auth/login" replace />;
  if (sessionStore.isAuthenticated()) {
    return <Navigate to={intent === 'member' ? ONB.join : createdId ? ONB.done : ONB.name} replace />;
  }

  const closed = config.isSuccess && !signupsAllowed(config.data);
  const links = footerLinksFromConfig(config.data);
  const sending = status === 'sending';

  const submit = async () => {
    if (sending || closed) return;
    setSysError(null);
    setMessage(null);
    const local = '0' + digits;
    if (!normalizeIraqiPhone(local)) {
      setStatus('invalid');
      return;
    }
    setStatus('sending');
    try {
      await onboardingStore.enrollment().start(local);
      setStatus('default');
      navigate(ONB.otp);
    } catch (e) {
      if (e instanceof AuthError) {
        switch (e.code) {
          case 'invalid_phone':
            setStatus('invalid');
            setMessage(e.message);
            return;
          case 'already_registered':
            setStatus('exists');
            return;
          case 'locked':
            setStatus('locked');
            setMessage(e.message);
            return;
          case 'network':
            setStatus('default');
            setSysError({ kind: 'network', ref: null });
            return;
          default:
            setStatus('error');
            setMessage(e.message);
            return;
        }
      }
      if (isApiError(e) && e.status > 0 && e.status < 500) {
        setStatus('error');
        setMessage(e.detail ?? e.message);
        return;
      }
      setStatus('default');
      setSysError({ ...failureOf(e) });
    }
  };

  const signIn = () => navigate('/auth/login', { state: { phone: '0' + digits } });
  const otherNumber = () => {
    setDigits('');
    setStatus('default');
    setMessage(null);
  };

  const error: WizardError | null = sysError ? { kind: sysError.kind, ref: sysError.ref, detail: sysError.detail, onRetry: () => void submit() } : null;

  const fieldError =
    status === 'invalid' ? (
      message ?? (
        <>
          {PHONE.invalid}
          <bdi dir="ltr" className="ltr">
            {PHONE.mask}
          </bdi>
        </>
      )
    ) : undefined;

  const exists = status === 'exists';
  const primary = exists ? (
    <PrimaryAction type="submit" size="lg" icon={LogIn} block={mobile} data-testid="onb-signin">
      {PHONE.signIn}
    </PrimaryAction>
  ) : (
    <PrimaryAction type="submit" size="lg" icon={ArrowLeft} block={mobile} loading={sending} disabled={closed} data-testid="onb-continue">
      {sending ? PHONE.sending : SHELL.continue}
    </PrimaryAction>
  );
  const secondary = exists ? (
    <Button variant="line" size="lg" block={mobile} data-testid="onb-other-number" onClick={otherNumber}>
      {PHONE.otherNumber}
    </Button>
  ) : undefined;
  const tertiary = exists || sending || closed ? undefined : <ConsentLine terms={links.terms} privacy={links.privacy} />;

  return (
    <Wizard
      screen="phone"
      testId="onb-phone"
      state={closed ? 'closed' : status}
      question={PHONE.title}
      sub={PHONE.sub}
      onSubmit={exists ? signIn : () => void submit()}
      error={error}
      enterHint={!exists && !sending && !closed}
      peek={PHONE.peek}
      panel={{ phase: 'identity', sub: 'phone' }}
      primary={primary}
      secondary={secondary}
      tertiary={tertiary}
    >
      {closed && (
        <Msg tone="info" testId="onb-closed">
          {PHONE.closed}
        </Msg>
      )}
      {notice && !closed && status === 'default' && (
        <Msg tone="info" testId="onb-phone-notice">
          {notice}
        </Msg>
      )}
      <PhoneBig
        data-testid="onb-phone-input"
        aria-label={PHONE.title}
        autoFocus
        value={digits}
        error={fieldError}
        disabled={closed}
        readOnly={sending}
        onChange={(d) => {
          setDigits(d);
          if (status !== 'default' && status !== 'sending') {
            setStatus('default');
            setMessage(null);
          }
        }}
      />
      {exists && (
        <Msg tone="info" testId="onb-phone-message">
          {PHONE.exists}
        </Msg>
      )}
      {status === 'locked' && message && (
        <Msg tone="warn" testId="onb-phone-message">
          {message}
        </Msg>
      )}
      {status === 'error' && message && <Msg testId="onb-phone-message">{message}</Msg>}
    </Wizard>
  );
}
